<template>
  <div :style="applyInlineStyles(element.design)" :class="element.extras.customClass">
    <div v-if="element.elementType === 'heading'">
      <component :is="`h${headingLevel}`" v-html="renderMarkdown(element.general.label || '')"></component>
    </div>

    <div v-else-if="element.elementType === 'paragraph'">
      <p v-html="renderMarkdown(element.general.label || '')"></p>
    </div>

    <div v-else-if="element.elementType === 'button'">
      <button
        :type="element.buttonType || 'submit'"
        class="btn btn-primary"
        :style="applyInlineStyles(element.design)"
        :disabled="isDisabled"
        v-bind="element.extras.customAttributes"
      >
        {{ element.general.label || 'Button' }}
      </button>
    </div>

    <div v-else>
      <label v-if="element.general.label" class="form-label" :for="inputId">
        <span v-html="renderMarkdown(element.general.label)"></span>
        <span v-if="isRequired" class="text-danger">*</span>
      </label>

      <div v-if="element.elementType === 'todoList'">
        <RenderedTodoList
          :settings="element.todoList || { mode: 'static', maxItems: -1, starterItems: [] }"
          :model-value="modelValue"
          :disabled="isDisabled"
          @update:model-value="emit('update', element.general.id, $event)"
        />
      </div>

      <div v-else-if="element.elementType === 'text' || element.elementType === 'email' || element.elementType === 'number' || element.elementType === 'date'">
        <div :class="{ 'input-group': hasPrePostText }">
          <span v-if="element.general.preText" class="input-group-text">
            {{ element.general.preText }}
          </span>

          <input
            :id="inputId"
            :type="element.elementType"
            class="form-control"
            :placeholder="element.general.placeholder || ''"
            :value="modelValue"
            @input="handleInput"
            :required="isRequired"
            :disabled="isDisabled"
            v-bind="getValidationAttributes()"
          />

          <span v-if="element.general.postText" class="input-group-text">
            {{ element.general.postText }}
          </span>
        </div>
      </div>

      <div v-else-if="element.elementType === 'textarea'">
        <textarea
          :id="inputId"
          class="form-control"
          :placeholder="element.general.placeholder || ''"
          :value="modelValue"
          @input="handleInput"
          :required="isRequired"
          :disabled="isDisabled"
          rows="4"
          v-bind="getValidationAttributes()"
        ></textarea>
      </div>

      <div v-else-if="element.elementType === 'select'">
        <select
          :id="inputId"
          class="form-select"
          :value="modelValue"
          @change="handleInput"
          :required="isRequired"
          :disabled="isDisabled"
        >
          <option value="">{{ element.general.placeholder || 'Select an option' }}</option>
          <option
            v-for="option in element.general.options"
            :key="option.value"
            :value="option.value"
          >
            {{ option.label }}
          </option>
        </select>
      </div>

      <div v-else-if="element.elementType === 'checkbox'">
        <div
          v-for="(option, idx) in element.general.options"
          :key="option.value"
          class="form-check"
        >
          <input
            :id="`${inputId}-${idx}`"
            class="form-check-input"
            type="checkbox"
            :value="option.value"
            :checked="isChecked(option.value)"
            @change="handleCheckboxChange(option.value, $event)"
            :disabled="isDisabled"
          />
          <label class="form-check-label" :for="`${inputId}-${idx}`">{{ option.label }}</label>
        </div>
      </div>

      <div v-else-if="element.elementType === 'radio'">
        <div
          v-for="(option, idx) in element.general.options"
          :key="option.value"
          class="form-check"
        >
          <input
            :id="`${inputId}-${idx}`"
            class="form-check-input"
            type="radio"
            :name="element.general.id"
            :value="option.value"
            :checked="modelValue === option.value"
            @change="handleInput"
            :disabled="isDisabled"
          />
          <label class="form-check-label" :for="`${inputId}-${idx}`">{{ option.label }}</label>
        </div>
      </div>

      <div v-else-if="element.elementType === 'file'">
        <input
          :id="inputId"
          type="file"
          class="form-control"
          @change="handleFileChange"
          :required="isRequired"
          :disabled="isDisabled"
        />
      </div>

      <div v-if="element.general.helpText" class="form-text">
        <span v-html="renderMarkdown(element.general.helpText)"></span>
      </div>

      <div v-if="showError" class="text-danger small mt-1">
        {{ element.general.errorMessage || 'This field is required' }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import type { Element, FormAnswer } from '../../types/form-builder';
import { applyInlineStyles } from '../../utils/form-helpers';
import { renderMarkdown } from '../../utils/markdown';
import RenderedTodoList from './RenderedTodoList.vue';

interface Props {
  element: Element;
  answers: FormAnswer;
  readOnly?: boolean;
}

interface Emits {
  (e: 'update', fieldId: string, value: any): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const showError = ref(false);

const modelValue = computed(() => props.answers[props.element.general.id]);

const inputId = computed(() => `input-${props.element.uid}`);

const isRequired = computed(() => {
  if (props.element.general.required) return true;

  if (props.element.logic && props.element.logic.length > 0) {
    for (const rule of props.element.logic) {
      if (rule.action === 'require' && evaluateRule(rule)) {
        return true;
      }
    }
  }

  return false;
});

const isDisabled = computed(() => {
  if (props.readOnly) {
    return true;
  }

  if (props.element.logic && props.element.logic.length > 0) {
    for (const rule of props.element.logic) {
      if (rule.action === 'disable' && evaluateRule(rule)) {
        return true;
      }
    }
  }

  return false;
});

const hasPrePostText = computed(() => {
  return !!(props.element.general?.preText || props.element.general?.postText);
});

const headingLevel = computed(() => {
  return props.element.design.fontSize === 'h1' ? 1 :
         props.element.design.fontSize === 'h2' ? 2 :
         props.element.design.fontSize === 'h3' ? 3 :
         props.element.design.fontSize === 'h4' ? 4 :
         props.element.design.fontSize === 'h5' ? 5 :
         props.element.design.fontSize === 'h6' ? 6 : 3;
});

function handleInput(event: Event) {
  const target = event.target as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;
  emit('update', props.element.general.id, target.value);
  showError.value = false;
}

function handleCheckboxChange(value: string | number | undefined, event: Event) {
  const target = event.target as HTMLInputElement;
  const currentValues = Array.isArray(modelValue.value) ? [...modelValue.value] : [];

  if (target.checked) {
    currentValues.push(value);
  } else {
    const index = currentValues.indexOf(value);
    if (index > -1) {
      currentValues.splice(index, 1);
    }
  }

  emit('update', props.element.general.id, currentValues);
}

function handleFileChange(event: Event) {
  const target = event.target as HTMLInputElement;
  const files = target.files;

  if (files && files.length > 0) {
    emit('update', props.element.general.id, files[0]);
  }
}

function isChecked(value: string | number | undefined): boolean {
  if (!modelValue.value) return false;
  if (Array.isArray(modelValue.value)) {
    return modelValue.value.includes(value);
  }
  return false;
}

function getValidationAttributes() {
  const attrs: Record<string, any> = {};

  if (props.element.extras.validation) {
    const validation = props.element.extras.validation;

    if (validation.pattern) {
      attrs.pattern = validation.pattern;
    }

    if (validation.minLength !== undefined) {
      attrs.minlength = validation.minLength;
    }

    if (validation.maxLength !== undefined) {
      attrs.maxlength = validation.maxLength;
    }

    if (validation.min !== undefined) {
      attrs.min = validation.min;
    }

    if (validation.max !== undefined) {
      attrs.max = validation.max;
    }
  }

  if (props.element.extras.customAttributes) {
    Object.assign(attrs, props.element.extras.customAttributes);
  }

  return attrs;
}

function evaluateRule(rule: any): boolean {
  if (rule.conditions.length === 0) return false;

  const results = rule.conditions.map((condition: any) => {
    const fieldValue = props.answers[condition.field];
    const compareValue = condition.value;

    switch (condition.operator) {
      case 'equals':
        return String(fieldValue) === String(compareValue);
      case 'notEquals':
        return String(fieldValue) !== String(compareValue);
      case 'contains':
        return String(fieldValue).includes(String(compareValue));
      case 'greaterThan':
        return Number(fieldValue) > Number(compareValue);
      case 'lessThan':
        return Number(fieldValue) < Number(compareValue);
      default:
        return false;
    }
  });

  if (rule.conjunction === 'and') {
    return results.every((r: boolean) => r);
  } else {
    return results.some((r: boolean) => r);
  }
}
</script>
